import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, Calendar as CalendarIcon } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { readingPlanService } from '../../../services/supabaseService';

const ReadingCalendar = ({ plan, currentDay, onDaySelect, className = '' }) => {
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(0); // Offset from start date
  const [completionStatus, setCompletionStatus] = useState({});

  useEffect(() => {
    if (plan?.id) {
      loadCalendarData();
    }
  }, [plan?.id, currentMonth]);

  const loadCalendarData = async () => {
    setLoading(true);
    try {
      const startDate = new Date(plan?.start_date || new Date());
      const totalDays = plan?.reading_plans?.duration_days || 365;
      
      // Calculate the month to display
      const displayDate = new Date(startDate);
      displayDate?.setMonth(displayDate?.getMonth() + currentMonth);
      
      // Get first and last day of the display month
      const firstDayOfMonth = new Date(displayDate.getFullYear(), displayDate.getMonth(), 1);
      const lastDayOfMonth = new Date(displayDate.getFullYear(), displayDate.getMonth() + 1, 0);
      
      // Calculate which plan days fall in this month
      const daysInMonth = [];
      const daysDiff = Math.floor((firstDayOfMonth - startDate) / (1000 * 60 * 60 * 24));
      
      for (let date = new Date(firstDayOfMonth); date <= lastDayOfMonth; date?.setDate(date?.getDate() + 1)) {
        const planDay = Math.floor((date - startDate) / (1000 * 60 * 60 * 24)) + 1;
        
        if (planDay >= 1 && planDay <= totalDays) {
          daysInMonth?.push({
            date: new Date(date),
            planDay: planDay,
            monthDay: date?.getDate(),
            isToday: planDay === currentDay,
            isPast: planDay < currentDay,
            isFuture: planDay > currentDay
          });
        } else {
          daysInMonth?.push({
            date: new Date(date),
            planDay: null,
            monthDay: date?.getDate(),
            isToday: false,
            isPast: false,
            isFuture: false
          });
        }
      }
      
      setCalendarData(daysInMonth);
      
      // Load completion status for days in this month
      await loadCompletionStatus(daysInMonth?.filter(d => d?.planDay)?.map(d => d?.planDay));
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompletionStatus = async (planDays) => {
    try {
      const statusMap = {};
      
      // Check completion for each day
      for (const dayNum of planDays) {
        const isComplete = await readingPlanService?.isDayCompleted(plan?.id, dayNum);
        statusMap[dayNum] = isComplete;
      }
      
      setCompletionStatus(statusMap);
    } catch (error) {
      console.error('Error loading completion status:', error);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => prev - 1);
  };

  const handleNextMonth = () => {
    const startDate = new Date(plan?.start_date || new Date());
    const endDate = new Date(startDate);
    endDate?.setDate(endDate?.getDate() + (plan?.reading_plans?.duration_days || 365) - 1);
    
    const nextMonthDate = new Date(startDate);
    nextMonthDate?.setMonth(nextMonthDate?.getMonth() + currentMonth + 1);
    
    if (nextMonthDate <= endDate) {
      setCurrentMonth(prev => prev + 1);
    }
  };

  const handleDayClick = (day) => {
    if (day?.planDay && onDaySelect) {
      onDaySelect(day?.planDay);
    }
  };

  const getMonthYear = () => {
    const startDate = new Date(plan?.start_date || new Date());
    const displayDate = new Date(startDate);
    displayDate?.setMonth(displayDate?.getMonth() + currentMonth);
    
    return displayDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getDayColor = (day) => {
    if (!day?.planDay) return 'bg-muted/20 text-muted-foreground/30 cursor-not-allowed';
    
    if (completionStatus?.[day?.planDay]) {
      return 'bg-success/20 text-success border-success/50 hover:bg-success/30 cursor-pointer';
    }
    
    if (day?.isToday) {
      return 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 cursor-pointer';
    }
    
    if (day?.isPast) {
      return 'bg-destructive/20 text-destructive border-destructive/50 hover:bg-destructive/30 cursor-pointer';
    }
    
    return 'bg-card text-foreground border-border hover:bg-muted/50 cursor-pointer';
  };

  // Get the first day of the month to calculate offset
  const getFirstDayOffset = () => {
    if (calendarData?.length === 0) return 0;
    return calendarData?.[0]?.date?.getDay() || 0;
  };

  if (loading) {
    return (
      <div className={`bg-card rounded-lg border border-border p-6 ${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <CalendarIcon className="w-8 h-8 text-primary animate-pulse mx-auto mb-2" />
            <p className="text-muted-foreground">Loading calendar...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg border border-border overflow-hidden ${className}`}>
      {/* Calendar Header */}
      <div className="border-b border-border p-4 bg-muted/30">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePreviousMonth}
            disabled={currentMonth <= 0}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <div className="text-center">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              {getMonthYear()}
            </h3>
            <p className="text-xs text-muted-foreground">
              {plan?.reading_plans?.name}
            </p>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNextMonth}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {/* Calendar Grid */}
      <div className="p-4">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
            <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-2">
          {/* Empty cells for offset */}
          {Array.from({ length: getFirstDayOffset() })?.map((_, index) => (
            <div key={`empty-${index}`} className="aspect-square" />
          ))}
          
          {/* Actual days */}
          {calendarData?.map((day, index) => (
            <button
              key={index}
              onClick={() => handleDayClick(day)}
              disabled={!day?.planDay}
              className={`aspect-square rounded-lg border transition-all duration-200 flex flex-col items-center justify-center p-1 ${getDayColor(day)}`}
            >
              <span className="text-xs font-medium">
                {day?.monthDay}
              </span>
              {day?.planDay && (
                <span className="text-[10px] opacity-70">
                  Day {day?.planDay}
                </span>
              )}
              {completionStatus?.[day?.planDay] && (
                <CheckCircle className="w-3 h-3 mt-0.5" />
              )}
            </button>
          ))}
        </div>
      </div>
      {/* Legend */}
      <div className="border-t border-border p-4 bg-muted/30">
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-success/20 border border-success/50" />
            <span className="text-muted-foreground">Completed</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-primary border border-primary" />
            <span className="text-muted-foreground">Current Day</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-destructive/20 border border-destructive/50" />
            <span className="text-muted-foreground">Missed</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-card border border-border" />
            <span className="text-muted-foreground">Upcoming</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadingCalendar;